// src/hooks/usePriorAuthGeneration.ts
// ──────────────────────────────────────────────────────────────
// After case creation, calls the generate-prior-auth-letter
// edge function using de-identified case data.
// Returns a function that can be called with the new case ID.
//
// Supports both initial PA and appeal letter generation.
// Appeal mode activates when denialReason is provided, and
// passes additional appeal context (denial text, date, original
// submission summary) to the edge function for escalated
// prompt construction.
// ──────────────────────────────────────────────────────────────

import { useCallback } from "react";

interface GenerateLetterParams {
  caseId: string;
  userId: string;
  diagnosesText: string;
  requestType: string;
  requestDetails: string;
  insuranceCompany: string;
  planType?: string;
  denialReason?: string;
  denialText?: string;
  denialDate?: string;
  originalSubmissionSummary?: string;
  conservativeTreatments?: string;
  additionalContext?: string;
  medicationName?: string;
}

// Guest mode params — no caseId/userId required
interface GenerateGuestLetterParams {
  diagnosesText: string;
  requestType: string;
  requestDetails: string;
  insuranceCompany: string;
  planType?: string;
  denialReason?: string;
  denialText?: string;
  denialDate?: string;
  originalSubmissionSummary?: string;
  conservativeTreatments?: string;
  additionalContext?: string;
  medicationName?: string;
}

interface GenerateLetterResult {
  success: boolean;
  letterId?: string;
  clinicalArgument?: {
    clinical_justification: string;
    criteria_citations: string[];
    literature_references: string[];
    risks_of_denial: string[];
    recommended_additions: string[];
    regulatory_leverage?: string[];
  };
  error?: string;
}

// ── Shared diagnosis parsing ─────────────────────────────────

function parseDiagnoses(diagnosesText: string): {
  diagnosisCodes: string[];
  descriptions: string[];
} {
  const diagParts = diagnosesText
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean);

  const codes: string[] = [];
  const descriptions: string[] = [];

  for (const part of diagParts) {
    const match = part.match(/^([A-Z]\d{2}\.?\d{0,4})\s*[-—:]\s*(.+)/i);
    if (match) {
      codes.push(match[1]);
      descriptions.push(match[2].trim());
    } else if (/^[A-Z]\d{2}/i.test(part)) {
      codes.push(part);
    } else {
      descriptions.push(part);
    }
  }

  return {
    diagnosisCodes: codes.length > 0 ? codes : descriptions,
    descriptions,
  };
}

// ── Medication name extraction ───────────────────────────────

function extractMedicationName(
  explicitName: string | undefined,
  requestType: string | undefined,
  requestDetails: string | undefined
): string | undefined {
  if (explicitName) return explicitName;
  if (requestType?.toLowerCase() !== "medication") return undefined;

  const medMatch = requestDetails?.match(/medication:\s*([^;,]+)/i);
  return medMatch ? medMatch[1].trim() : undefined;
}

// ── Main hook ────────────────────────────────────────────────

export function usePriorAuthGeneration() {
  const generateLetter = useCallback(
    async (params: GenerateLetterParams): Promise<GenerateLetterResult> => {
      try {
        const { diagnosisCodes, descriptions } = parseDiagnoses(
          params.diagnosesText
        );

        if (diagnosisCodes.length === 0 && !params.requestDetails) {
          return { success: false, error: "No diagnosis or request details" };
        }

        // Determine request type for letter template
        const isAppeal = params.denialReason ? "appeal" : "initial";

        const medicationName = extractMedicationName(
          params.medicationName,
          params.requestType,
          params.requestDetails
        );

        const resp = await fetch(
          `${
            import.meta.env.VITE_SUPABASE_URL
          }/functions/v1/generate-prior-auth-letter`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              case_id: params.caseId,
              user_id: params.userId,
              diagnosis_codes: diagnosisCodes,
              diagnosis_descriptions: descriptions.length
                ? descriptions
                : undefined,
              procedure_description: params.requestDetails,
              medication_name: medicationName,
              insurance_company: params.insuranceCompany,
              plan_type: params.planType,
              request_type: isAppeal,
              denial_reason: params.denialReason || undefined,
              denial_text: params.denialText || undefined,
              denial_date: params.denialDate || undefined,
              original_submission_summary:
                params.originalSubmissionSummary || undefined,
              conservative_treatments:
                params.conservativeTreatments || undefined,
              clinical_notes: params.additionalContext || undefined,
            }),
          }
        );

        const data = await resp.json();
        if (!resp.ok) {
          throw new Error(data.error || "Letter generation failed");
        }

        return {
          success: true,
          letterId: data.letter_id,
          clinicalArgument: data.clinical_argument,
        };
      } catch (e: any) {
        console.error("[PriorAuth] Generation failed:", e);
        return {
          success: false,
          error: e.message || "Failed to generate letter",
        };
      }
    },
    []
  );

  // Guest generation — same logic but with guest=true flag
  const generateGuestLetter = useCallback(
    async (
      params: GenerateGuestLetterParams
    ): Promise<GenerateLetterResult> => {
      try {
        const { diagnosisCodes, descriptions } = parseDiagnoses(
          params.diagnosesText
        );

        if (diagnosisCodes.length === 0 && !params.requestDetails) {
          return { success: false, error: "No diagnosis or request details" };
        }

        const isAppeal = params.denialReason ? "appeal" : "initial";

        const medicationName = extractMedicationName(
          params.medicationName,
          params.requestType,
          params.requestDetails
        );

        const resp = await fetch(
          `${
            import.meta.env.VITE_SUPABASE_URL
          }/functions/v1/generate-prior-auth-letter`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              guest: true,
              diagnosis_codes: diagnosisCodes,
              diagnosis_descriptions: descriptions.length
                ? descriptions
                : undefined,
              procedure_description: params.requestDetails,
              medication_name: medicationName,
              insurance_company: params.insuranceCompany,
              plan_type: params.planType,
              request_type: isAppeal,
              denial_reason: params.denialReason || undefined,
              denial_text: params.denialText || undefined,
              denial_date: params.denialDate || undefined,
              original_submission_summary:
                params.originalSubmissionSummary || undefined,
              conservative_treatments:
                params.conservativeTreatments || undefined,
              clinical_notes: params.additionalContext || undefined,
            }),
          }
        );

        const data = await resp.json();
        if (!resp.ok) {
          throw new Error(data.error || "Letter generation failed");
        }

        return {
          success: true,
          clinicalArgument: data.clinical_argument,
        };
      } catch (e: any) {
        console.error("[PriorAuth] Guest generation failed:", e);
        return {
          success: false,
          error: e.message || "Failed to generate letter",
        };
      }
    },
    []
  );

  return { generateLetter, generateGuestLetter };
}